import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ScanLine, FileCheck2, Landmark, Search, Camera, HandshakeIcon, ArrowRight, Car, Building2, Wrench, Briefcase } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ThomVerifiedStamp from "@/components/ThomVerifiedStamp";

const HERO_IMG = "https://images.unsplash.com/photo-1643142314913-0cf633d9bbb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwc3V2JTIwY2FyJTIwc2hvd3Jvb218ZW58MHx8fHwxNzg0NzM2Mjc1fDA&ixlib=rb-4.1.0&q=85";
const MECHANIC_IMG = "/brand/live-inspection.jpg";
const HANDSHAKE_IMG = "/brand/escrow-protected.jpg";

const Step = ({ n, title, body, Icon }) => (
  <div className="p-6 border border-border bg-card">
    <div className="flex items-center justify-between mb-4">
      <span className="mono text-xs text-muted-foreground">STEP {n.toString().padStart(2, "0")}</span>
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <div className="display text-xl mb-2">{title}</div>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

const Module = ({ name, tagline, live, Icon }) => (
  <div className={`p-6 border border-border ${live ? "bg-card" : "bg-secondary/50"}`}>
    <div className="flex items-center justify-between mb-4">
      <Icon className={`h-6 w-6 ${live ? "text-primary" : "text-muted-foreground"}`} />
      <span className={`mono text-[10px] uppercase tracking-widest ${live ? "text-primary" : "text-muted-foreground"}`}>
        {live ? "Live" : "Coming"}
      </span>
    </div>
    <div className="display text-lg">{name}</div>
    <p className="text-xs text-muted-foreground mt-1">{tagline}</p>
  </div>
);

export default function Landing() {
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="thom-seal" data-testid="hero-badge">AutoVerify™ · Live Across Africa</span>
            </div>
            <h1 className="display text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              Never buy a
              <br />
              <span className="text-primary">stolen, cloned, tampered</span>
              <br />
              or fraudulently represented
              <br />
              asset again.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Independent verification across Africa before you commit. THOM Verify™ physically
              inspects the asset, cross-checks statutory registrars and police records where
              authorised, issues a tamper-evident QR-verified certificate, and holds your deposit
              in escrow until you accept.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/marketplace" data-testid="hero-cta-browse" className="btn-primary">
                Browse Verified Cars <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="hero-cta-sell" className="btn-outline">
                List &amp; Verify Your Car
              </Link>
              <Link to="/verify" data-testid="hero-cta-verify" className="btn-outline">
                <ScanLine className="h-4 w-4" /> Verify a Certificate
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              <div>
                <div className="display text-3xl">10-pt</div>
                <div className="kicker mt-1">Legal · Mechanical Checks</div>
              </div>
              <div>
                <div className="display text-3xl">USD</div>
                <div className="kicker mt-1">Escrow Protected</div>
              </div>
              <div>
                <div className="display text-3xl">QR</div>
                <div className="kicker mt-1">Certificate Validated</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <img src={HERO_IMG} alt="Verified SUV" className="w-full h-[520px] object-cover border border-border" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 55%)" }} />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="absolute -left-8 -bottom-8 hidden lg:block"
              >
                <ThomVerifiedStamp size={180} verificationStatus="verified" trustScore={94} rating="Excellent" certificateId="TV-2026-004563" showTooltip={false} lazy={false} />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-y border-border bg-secondary/40 py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="max-w-3xl">
            <div className="kicker mb-3">The THOM Verify™ Process</div>
            <h2 className="display text-4xl md:text-5xl">Physical, legal &amp; documentary — all in one pass.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4 mt-10">
            <Step n={1} Icon={Search} title="Seller books an inspection" body="Once a car is listed, the seller requests a THOM Verification and a booking is created with a nearby inspector." />
            <Step n={2} Icon={Camera} title="On-site 10-point audit" body="Our inspector performs a live inspection: VIN, engine number, statutory duty and police clearance for the country of registration, mileage, mechanical, structural, tyres, interior — with photo evidence per item." />
            <Step n={3} Icon={FileCheck2} title="Trust Score &amp; Certificate" body="A THOM Verify™ Trust Score is calculated with a clear breakdown. If verified, a QR-linked, digitally signed certificate is issued." />
            <Step n={4} Icon={HandshakeIcon} title="Escrow-protected purchase" body="Buyers commit funds into THOM Escrow™. Funds are only released once the buyer inspects and accepts the vehicle." />
          </div>
        </div>
      </section>

      {/* IMAGE BAND */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative">
            <img src={MECHANIC_IMG} alt="Certified THOM Verify inspector performing a comprehensive vehicle inspection." className="w-full h-[420px] object-cover border border-border" loading="lazy" />
            <div className="absolute top-4 left-4 thom-seal">Live Inspection</div>
          </div>
          <div>
            <div className="kicker mb-2">Trust as a Service</div>
            <h3 className="display text-4xl">Independent. Evidence-based. Signed.</h3>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Every check, every photograph, every note is tied to an inspector ID and timestamped.
              Reports are versioned, cryptographically hashed, and digitally signed —
              so a printed PDF can always be re-validated against the source of truth.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-start gap-3"><FileCheck2 className="h-4 w-4 mt-0.5 text-primary" /> Interactive HTML report + tamper-evident PDF</li>
              <li className="flex items-start gap-3"><ScanLine className="h-4 w-4 mt-0.5 text-primary" /> QR-verified digital certificate with public verification page</li>
              <li className="flex items-start gap-3"><Landmark className="h-4 w-4 mt-0.5 text-primary" /> Vehicle Passport™ – full history: inspections, certificates, escrow</li>
            </ul>
          </div>
        </div>
      </section>

      {/* MODULES / ECOSYSTEM */}
      <section className="bg-secondary/40 border-y border-border py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="kicker mb-3">The THOM Verify™ Ecosystem</div>
          <h2 className="display text-4xl md:text-5xl max-w-3xl">One verification platform. Every high-value asset class.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            <Module Icon={Car} live name="AutoVerify™" tagline="Vehicles — MVP live." />
            <Module Icon={Building2} name="PropertyVerify™" tagline="Title deeds, structural, boundary." />
            <Module Icon={Wrench} name="EquipmentVerify™" tagline="Serial no., ownership, condition." />
            <Module Icon={Briefcase} name="BusinessVerify™" tagline="Registration, tax, beneficial ownership." />
          </div>
        </div>
      </section>

      {/* ESCROW BAND */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="kicker mb-2">THOM Escrow™</div>
            <h3 className="display text-4xl">Money moves only when both parties are protected.</h3>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Buyers commit a deposit into a platform-managed escrow using any supported African
              payment method — mobile money, instant EFT, bank transfer, cash at a THOM office, or
              global card and wallet rails. Funds are released to the seller only after the buyer
              accepts the asset — with our admins as neutral arbiter on any dispute.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                "Paynow · EcoCash", "OneMoney", "InnBucks", "M-Pesa", "MTN MoMo", "Airtel Money",
                "Orange Money", "Ozow · Instant EFT", "PayFast", "Flutterwave", "DPO Group",
                "Bank Transfer", "Cash @ THOM", "Stripe · phase 2", "PayPal · phase 2",
              ].map((g) => (
                <span key={g} className="mono text-[11px] px-3 py-1.5 border border-border rounded-sm bg-card">{g}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={HANDSHAKE_IMG} alt="Professional vehicle transaction secured through THOM Verify escrow protection." className="w-full h-[420px] object-cover border border-border" loading="lazy" />
            <div className="absolute bottom-4 right-4 thom-seal">Escrow Protected</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border py-24 bg-primary text-primary-foreground">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <h3 className="display text-4xl md:text-5xl">Ready to trade with confidence?</h3>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/register" data-testid="footer-cta-register" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-sm hover:bg-white/90">Create an account</Link>
            <Link to="/marketplace" data-testid="footer-cta-market" className="inline-flex items-center gap-2 px-6 py-3 border border-white/50 text-white font-semibold rounded-sm hover:bg-white/10">Browse verified cars</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
